import { useState, useContext, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Modal, ModalDialog, ModalClose,
  Typography, Stack, Input, Textarea, Button,
  Box, Chip, CircularProgress,
  Autocomplete, AutocompleteOption, ListItemContent,
  FormControl,
  FormLabel,
} from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import { LayoutContext } from "../../context";
import { APIClientCreateShortApplication, APISearchCities } from "../../api";
import MaskedInput from "../JoyMaskedInput";


/* ── Автокомплит города для быстрой заявки (без «моих городов») ── */

// Сентинел опции "Моего города нет" (показывается когда ввода нет)
const CUSTOM_CITY_OPTION_Q = { id: '__CUSTOM__', name: '✏️ Моего города нет в списке' };

// Опция inline-добавления текущего введённого значения как нового города
const makeAddNewCityOptionQ = (cityName) => ({
  id: '__NEW__',
  name: cityName,
  isNew: true,
});

function CityAutocompleteQuick({ value, customCityName, onChange, onCustomChange }) {
  const [options, setOptions] = useState([]);
  const [initialCities, setInitialCities] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  // showCustomInput включается только когда пользователь ткнул в "Моего города нет"
  // и явно хочет ввести в отдельное поле. Inline-вариант ("+ Использовать «...»")
  // его НЕ включает.
  const [showCustomInput, setShowCustomInput] = useState(false);
  const searchTimer = useRef(null);

  // При монтировании — загружаем начальные города (пустой запрос)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const cities = await APISearchCities('', 15);
      if (!cancelled) {
        setInitialCities(Array.isArray(cities) ? cities : []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Поиск при вводе (debounce 400 мс)
  useEffect(() => {
    if (!inputValue || inputValue.length < 2) {
      setOptions([]);
      return;
    }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setLoading(true);
      const results = await APISearchCities(inputValue, 15);
      setOptions(Array.isArray(results) ? results : []);
      setLoading(false);
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [inputValue]);

  // Формируем список опций для выпадашки
  const displayOptions = useMemo(() => {
    const query = (inputValue || '').trim();
    const hasQuery = query.length >= 2;

    let base = hasQuery && options.length > 0 ? options : initialCities;
    base = Array.isArray(base) ? base : [];

    if (hasQuery) {
      // Если ввели текст, но точного совпадения нет — предлагаем добавить как новый
      const exactMatch = base.some(c =>
        (c?.name || '').toLowerCase().trim() === query.toLowerCase()
      );
      if (!exactMatch) {
        return [...base, makeAddNewCityOptionQ(query)];
      }
      return base;
    }

    // Ввода нет — даём ещё опцию "Моего города нет" в самом низу
    return [...base, CUSTOM_CITY_OPTION_Q];
  }, [options, initialCities, inputValue]);

  // Текущий выбранный объект
  const selectedCity = (() => {
    if (showCustomInput) return CUSTOM_CITY_OPTION_Q;
    // Если задан custom_city_name, но id из справочника не выбран — значит выбран __NEW__
    if (!value && customCityName) {
      return makeAddNewCityOptionQ(customCityName);
    }
    if (!value) return null;
    return (
      displayOptions.find(c => c.id === value)
      || initialCities.find(c => c.id === value)
      || null
    );
  })();

  return (
    <Box>
      <Autocomplete
        placeholder="Введите город или выберите"
        value={selectedCity}
        onChange={(_, newVal) => {
          if (!newVal) {
            setShowCustomInput(false);
            onChange(null);
            onCustomChange?.('');
            return;
          }
          if (newVal.id === '__CUSTOM__') {
            setShowCustomInput(true);
            onChange(null);
            return;
          }
          if (newVal.id === '__NEW__') {
            setShowCustomInput(false);
            onChange(null);
            onCustomChange?.(newVal.name);
            return;
          }
          // Обычный город из справочника
          setShowCustomInput(false);
          onCustomChange?.('');
          onChange(newVal.id);
        }}
        inputValue={inputValue}
        onInputChange={(_, val) => setInputValue(val)}
        options={displayOptions}
        getOptionLabel={(opt) => opt?.name || ''}
        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
        loading={loading}
        noOptionsText={inputValue ? "Загрузка..." : "Начните вводить название"}
        startDecorator={<SearchRoundedIcon sx={{ fontSize: 18, color: 'text.tertiary' }} />}
        endDecorator={loading ? <CircularProgress size="sm" sx={{ mr: 1 }} /> : null}
        slotProps={{ listbox: { sx: { maxHeight: 240 } } }}
        renderOption={(props, option) => {
          const isCustomMarker = option.id === '__CUSTOM__';
          const isNewInline    = option.id === '__NEW__';

          let sx = {};
          if (isCustomMarker) {
            sx = {
              borderTop: '1px solid', borderColor: 'divider',
              fontStyle: 'italic',
              '& *': { color: 'primary.500', fontWeight: 600 },
            };
          } else if (isNewInline) {
            sx = {
              borderTop: '1px solid', borderColor: 'divider',
              bgcolor: 'primary.50',
              '&:hover': { bgcolor: 'primary.100' },
            };
          }

          return (
            <AutocompleteOption {...props} key={option.id} sx={sx}>
              <ListItemContent>
                {isNewInline ? (
                  <Box>
                    <Typography level="body-sm" sx={{ fontWeight: 700, color: 'primary.700' }}>
                      + Использовать «{option.name}» как новый город
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.25 }}>
                      💡 Менеджер проверит и добавит в базу
                    </Typography>
                  </Box>
                ) : (
                  <Typography level="body-sm" fontWeight={600}>{option.name}</Typography>
                )}
              </ListItemContent>
            </AutocompleteOption>
          );
        }}
        sx={{
          '& .MuiAutocomplete-input': { fontSize: '0.875rem' },
          borderRadius: "lg",
        }}
      />

      {/* Чип "Новый город: ..." когда выбран inline-вариант */}
      {!value && customCityName && !showCustomInput && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75 }}>
          <Chip size="sm" variant="soft" color="primary" startDecorator={<span>🆕</span>}>
            Новый город: {customCityName}
          </Chip>
          <Button
            variant="plain"
            color="neutral"
            size="sm"
            onClick={() => {
              onCustomChange?.('');
              onChange(null);
              setInputValue('');
            }}
            sx={{ minHeight: 'auto', px: 0.5, py: 0.25 }}
          >
            Сбросить
          </Button>
        </Stack>
      )}

      {/* Поле для ручного ввода — открывается только по клику на "Моего города нет" */}
      {showCustomInput && (
        <Box sx={{ mt: 1 }}>
          <Input
            size="lg"
            placeholder="Название города"
            value={customCityName || ''}
            onChange={(e) => onCustomChange?.(e.target.value)}
            sx={{ borderRadius: 'lg' }}
          />
          <Typography level="body-xs" color="warning" sx={{ mt: 0.5 }}>
            💡 Город будет добавлен в базу автоматически после проверки менеджером
          </Typography>
        </Box>
      )}
    </Box>
  );
}


/* ── Модалка быстрой заявки ── */
export default function QuickOrderModal({ open, onClose, calculatorAmount = null }) {
  const { account } = useContext(LayoutContext);

  const [form, setForm] = useState({
    phone: "",
    comment: "",
    city: null,
    custom_city_name: "",
    desired_date: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Уникальный ключ "сессии" модалки — увеличивается при каждом открытии.
  // Используется для принудительного ремоунта CityAutocompleteQuick, чтобы
  // сбросить его внутренний state (inputValue, options, showCustomInput).
  const [sessionKey, setSessionKey] = useState(0);

  // Сброс при открытии
  useEffect(() => {
    if (open) {
      setForm({
        phone: account?.phone || "",
        comment: "",
        city: null,
        custom_city_name: "",
        desired_date: "",
      });
      setError("");
      setSuccess(false);
      setSessionKey(k => k + 1);
    }
  }, [open, account]);

  const handleSubmit = async () => {
    if (submitting) return; // защита от двойного клика
    if (!form.phone.trim()) {
      setError("Укажите номер телефона");
      return;
    }
    setSubmitting(true);
    setError("");

    const payload = {
      phone: form.phone.trim(),
      comment: form.comment,
      city: form.city || null,
      desired_date: form.desired_date || null,
    };
    if (!form.city && form.custom_city_name.trim()) {
      payload.custom_city_name = form.custom_city_name.trim();
    }
    if (calculatorAmount) {
      payload.calculator_amount = calculatorAmount;
    }

    try {
      const res = await APIClientCreateShortApplication(payload);
      if (res?.status === "error") {
        setError(typeof res.detail === "string" ? res.detail : "Ошибка при создании заявки");
        return;
      }
      setSuccess(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        variant="outlined"
        sx={{
          maxWidth: 440,
          width: "95vw",
          borderRadius: "28px",
          p: { xs: 2.5, sm: 3.5 },
          boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
          // чтобы выпадашка Autocomplete не обрезалась
          overflow: "visible",
        }}
      >
        <ModalClose />

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
            >
              <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 56, color: "success.500" }} />
                <Typography level="h4" sx={{ fontWeight: 700 }}>
                  Заявка отправлена!
                </Typography>
                <Typography level="body-md" sx={{ color: "neutral.600", textAlign: "center" }}>
                  Менеджер свяжется с вами в ближайшее время
                </Typography>
                {calculatorAmount && (
                  <Chip variant="soft" color="primary" size="lg">
                    Расчёт: {Number(calculatorAmount).toLocaleString("ru-RU")} ₽
                  </Chip>
                )}
                <Button
                  variant="solid"
                  color="primary"
                  onClick={onClose}
                  sx={{ mt: 1, borderRadius: "xl", px: 4 }}
                >
                  Отлично
                </Button>
              </Stack>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <Typography level="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                Быстрая заявка
              </Typography>
              <Typography level="body-sm" sx={{ color: "neutral.500", mb: 2.5 }}>
                Оставьте контакт — мы перезвоним и уточним детали
              </Typography>

              {calculatorAmount && (
                <Box
                  sx={{
                    mb: 2.5, p: 1.5,
                    borderRadius: "lg",
                    background: "linear-gradient(135deg, rgba(99,102,241,0.08), rgba(192,132,252,0.08))",
                    border: "1px solid rgba(99,102,241,0.2)",
                  }}
                >
                  <Typography level="body-xs" sx={{ color: "neutral.500", mb: 0.25 }}>
                    Предварительная стоимость
                  </Typography>
                  <Typography level="h4" sx={{ fontWeight: 800, color: "primary.600" }}>
                    {Number(calculatorAmount).toLocaleString("ru-RU")} ₽
                  </Typography>
                </Box>
              )}

              <Stack spacing={2}>
                {/* Телефон */}
                <FormControl>
                    <FormLabel>Номер телефона</FormLabel>
                    <MaskedInput
                        mask="+7 (000) 000-00-00"
                        placeholder="+7 (000) 000-00-00"
                        value={form.phone}
                        onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                        startDecorator={<PhoneRoundedIcon />}
                        size="lg"
                        sx={{ fontSize: "1.05rem", fontWeight: 600, letterSpacing: "0.03em" }}
                    />
                </FormControl>

                {/* Город — Autocomplete с поиском */}
                <FormControl>
                  <FormLabel>Город</FormLabel>
                  <CityAutocompleteQuick
                    key={sessionKey}
                    value={form.city}
                    customCityName={form.custom_city_name}
                    onChange={(v) => setForm(p => ({ ...p, city: v, custom_city_name: "" }))}
                    onCustomChange={(name) => setForm(p => ({ ...p, custom_city_name: name, city: null }))}
                  />
                </FormControl>

                {/* Дата */}
                <FormControl>
                  <FormLabel>Желаемая дата</FormLabel>
                  <Input
                    type="date"
                    value={form.desired_date}
                    onChange={(e) => setForm(f => ({ ...f, desired_date: e.target.value }))}
                    startDecorator={<CalendarMonthRoundedIcon />}
                    size="lg"
                    slotProps={{ input: { min: new Date().toISOString().split("T")[0] } }}
                  />
                </FormControl>

                {/* Комментарий */}
                <FormControl>
                  <FormLabel>Комментарий</FormLabel>
                  <Textarea
                    placeholder="Опишите задачу или пожелания"
                    value={form.comment}
                    onChange={(e) => setForm(f => ({ ...f, comment: e.target.value }))}
                    minRows={2}
                    maxRows={4}
                    size="lg"
                  />
                </FormControl>

                {error && (
                  <Typography level="body-sm" color="danger">
                    {error}
                  </Typography>
                )}

                <Button
                  size="lg"
                  loading={submitting}
                  disabled={submitting}
                  onClick={handleSubmit}
                  sx={{
                    fontWeight: 700,
                    borderRadius: "xl",
                    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    "&:hover": { background: "linear-gradient(135deg, #4f46e5, #7c3aed)" },
                  }}
                >
                  Отправить заявку
                </Button>
              </Stack>
            </motion.div>
          )}
        </AnimatePresence>
      </ModalDialog>
    </Modal>
  );
}